import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import MarketplaceTable from './components/MarketplaceTable';
import MarketplaceCards from './components/MarketplaceCards';
import SugarBookingModal from './components/SugarBookingModal';
import BundleBookingModal from './components/BundleBookingModal';
import PaymentInfoModal from './components/PaymentInfoModal';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeFactoryName, normalizeGrade } from '../live-tender-info/matching';
import { Helmet } from 'react-helmet';

/**
 * Map a quota row (joined with factory data) to the load shape expected by
 * MarketplaceTable / MarketplaceCards / SugarTruckMultiModal.
 * Data comes 100% from Supabase — no hardcoded fallbacks.
 */
const quotaToLoad = (quota, factory) => ({
  id: quota?.id,
  date: quota?.added_date || quota?.created_at?.slice(0, 10) || new Date()?.toISOString()?.slice(0, 10),
  offerId: quota?.offer_id || `SH-${quota?.factory_short_code}-${quota?.grade}`,
  readableOfferId: quota?.readable_offer_id || null,
  sellerName: quota?.seller_name || null,
  product: 'White Refined Sugar',
  millName: factory?.name || quota?.factory_name || '',
  millCode: quota?.factory_short_code || '',
  state: factory?.state || '',
  stateName: factory?.state || '',
  city: factory?.city || factory?.location?.split(',')?.[0]?.trim() || '',
  deliveryFrom: factory?.location || factory?.city || quota?.factory_location || '',
  pincode: quota?.loading_pincode || factory?.pincode || '',
  loadingPincode: quota?.loading_pincode || '',
  distanceFromBuyer: 0,
  paymentDate: quota?.original_payment_by_date || '',
  liftingDate: quota?.original_lifting_by_date || '',
  grade: quota?.grade || '',
  season: '2024-25',
  packSize: '50 kg bags',
  quantity: Number(quota?.quantity) || 0,
  rate: Number(quota?.price_per_ton) || 0,
  trucksAvailable: 0,
  insuranceAvailable: false,
  transportationRatePerTon: 0,
  productDescription: factory?.product_description || '',
  zone: factory?.zone || '',
  factoryPhotoUrl: factory?.factory_photo_url || '',
  productPhotoUrl: factory?.product_photo_url || '',
  packingPhotoUrl: factory?.packing_photo_url || '',
  availabilityStatus: quota?.availability_status || 'available',
});

const SugarMarketplaceDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, userProfile } = useAuth();
  const [viewMode, setViewMode] = useState('table');
  const [userRole, setUserRole] = useState(null);
  const [filters, setFilters] = useState({
    state: 'all',
    mill: 'all',
    grade: 'all',
    distanceSlab: 'all',
    search: '',
    minPrice: '',
    maxPrice: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [availableMills, setAvailableMills] = useState([]);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Modal states
  const [sugarBookingModal, setSugarBookingModal] = useState({ isOpen: false, data: null });
  const [bundleBookingModal, setBundleBookingModal] = useState({ isOpen: false, data: null, noTrucks: false });
  const [paymentInfoModal, setPaymentInfoModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [quotaCheckLoading, setQuotaCheckLoading] = useState(false);
  const [pendingApprovalModal, setPendingApprovalModal] = useState(false);

  const [filteredLoads, setFilteredLoads] = useState([]);

  // Mandi -> Marketplace offer continuation (see live-tender-info/matching.js
  // buildMarketplaceBuyUrl). highlightedLoadId drives scroll+highlight in
  // MarketplaceTable/Cards; offerUnavailable shows a non-blocking banner when
  // the offer Mandi pointed at can no longer be resolved.
  const [highlightedLoadId, setHighlightedLoadId] = useState(null);
  const [offerUnavailable, setOfferUnavailable] = useState(false);
  const mandiResolveAttemptedRef = useRef(false);

  // Initialize user role on mount
  useEffect(() => {
    const role = localStorage.getItem('userRole');
    setUserRole(role);
  }, []);

  /**
   * Load quotas from Supabase and join with factory data.
   * This is the ONLY source of truth — no dummy data fallback.
   */
  const fetchLoads = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Fetch available quotas AND pending_approval quotas (both visible in marketplace)
      const { data: quotas, error: quotaError } = await supabase?.from('quotas')?.select('*')?.in('availability_status', ['available'])?.order('created_at', { ascending: false });

      if (quotaError) throw quotaError;

      // Belt-and-suspenders: never show a quota that's exhausted or past its lifting
      // date, regardless of what availability_status says. today's date is computed
      // once per fetch, compared as plain 'YYYY-MM-DD' strings (original_lifting_by_date
      // is stored in that exact format by the sync trigger, so string comparison is safe
      // and avoids timezone edge cases from constructing Date objects).
      const todayStr = new Date()?.toISOString()?.slice(0, 10);
      const liveQuotas = (quotas || [])?.filter((q) => {
        if (Number(q?.quantity) <= 0) return false;
        if (q?.original_lifting_by_date && q?.original_lifting_by_date < todayStr) return false;
        return true;
      });

      if (liveQuotas?.length === 0) {
        setLoads([]);
        setLoading(false);
        return;
      }

      // Fetch all active factories for joining
      const { data: factories, error: factoryError } = await supabase?.from('factories')?.select('*')?.eq('status', 'Active');

      if (factoryError) throw factoryError;

      // Build a map: short_code → factory row for fast lookup
      const factoryMap = {};
      (factories || [])?.forEach(f => {
        if (f?.short_code) factoryMap[f.short_code] = f;
        if (f?.name) factoryMap[f.name] = f;
      });

      // Map each quota to a load, joining with factory data
      // Only include quotas where an active factory match exists (factories.status = 'Active')
      const mapped = liveQuotas
        ?.map(quota => {
          const factory = factoryMap?.[quota?.factory_short_code] || factoryMap?.[quota?.factory_name] || null;
          return { load: quotaToLoad(quota, factory), factory };
        })
        ?.filter(({ factory }) => factory !== null)
        ?.map(({ load }) => load);

      setLoads(mapped);
    } catch (err) {
      console.error('fetchLoads error:', err?.message);
      setLoadError(err?.message || 'Failed to load marketplace data');
      setLoads([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch + real-time subscription
  useEffect(() => {
    fetchLoads();

    // Subscribe to quota changes for live sync
    const channel = supabase?.channel('marketplace_quotas')?.on('postgres_changes', { event: '*', schema: 'public', table: 'quotas' }, () => {
        fetchLoads();
      })?.on('postgres_changes', { event: '*', schema: 'public', table: 'factories' }, () => {
        fetchLoads();
      })?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  // Resolve a Mandi-originated offer once `loads` has finished its first fetch.
  // Only ever runs once per page load (mandiResolveAttemptedRef) so the
  // real-time `loads` refresh above can't re-trigger it or fight the URL
  // rewrite below.
  //
  // Step 1 (canonical): ?offer=<quotas.offer_id>&source=mandi — exact lookup.
  // Step 2 (initial Mandi context): ?factory=&grade=&source=mandi — resolved
  // via the SAME normalizeFactoryName/normalizeGrade used by Live Tender Info
  // ↔ Mandi matching, never duplicated here. quoteId (Mandi's mill_price_quotes
  // id) rides along for traceability only and is never used to look anything up.
  //
  // On a match, the URL is replaced with the canonical ?offer=<offer_id> form
  // (mirrors the autoBuy URL-rewrite already used in mandi/factory/index.jsx)
  // so a refresh/share from this point on is an exact lookup, not a fuzzy one.
  useEffect(() => {
    if (loading) return;
    if (mandiResolveAttemptedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    if (source !== 'mandi') return;

    const offerParam = params.get('offer');
    const factoryParam = params.get('factory');
    const gradeParam = params.get('grade');
    if (!offerParam && !factoryParam) return;

    mandiResolveAttemptedRef.current = true;

    let match = offerParam ? (loads.find((l) => l?.offerId === offerParam) || null) : null;

    if (!match && factoryParam) {
      const targetFactory = normalizeFactoryName(factoryParam);
      const targetGrade = gradeParam ? normalizeGrade(gradeParam) : null;
      match = loads.find((l) =>
        normalizeFactoryName(l?.millName) === targetFactory &&
        (!targetGrade || normalizeGrade(l?.grade) === targetGrade)
      ) || null;
    }

    if (match) {
      setHighlightedLoadId(match.id);
      const canonical = new URLSearchParams();
      canonical.set('offer', match.offerId);
      canonical.set('source', 'mandi');
      navigate(`/sugar-marketplace-dashboard?${canonical.toString()}`, { replace: true });
    } else {
      setOfferUnavailable(true);
    }
  }, [loading, loads, navigate]);

  // Update available mills when loads change
  useEffect(() => {
    const millOptions = loads?.filter(l => l?.millCode)?.reduce((acc, l) => {
        if (!acc?.find(m => m?.value === l?.millCode)) {
          acc?.push({
            value: l?.millCode,
            label: `${l?.millCode} – ${l?.millName} (${l?.city})`,
          });
        }
        return acc;
      }, []);

    if (filters?.state === 'all') {
      setAvailableMills([{ value: 'all', label: 'All Factories' }, ...millOptions]);
    } else {
      const stateMills = millOptions?.filter(m => {
        const load = loads?.find(l => l?.millCode === m?.value);
        return load?.state === filters?.state;
      });
      setAvailableMills([{ value: 'all', label: 'All Factories' }, ...stateMills]);

      if (filters?.mill !== 'all' && !stateMills?.some(m => m?.value === filters?.mill)) {
        setFilters(prev => ({ ...prev, mill: 'all' }));
      }
    }
  }, [filters?.state, loads]);

  // Apply filters and sorting to loads
  useEffect(() => {
    let result = [...loads];

    if (filters?.state !== 'all') {
      result = result?.filter(load => load?.state === filters?.state);
    }

    if (filters?.mill !== 'all') {
      result = result?.filter(load => load?.millCode === filters?.mill);
    }

    if (filters?.grade !== 'all') {
      result = result?.filter(load => load?.grade === filters?.grade);
    }

    if (filters?.distanceSlab !== 'all') {
      result = result?.filter(load => {
        const distance = load?.distanceFromBuyer;
        switch (filters?.distanceSlab) {
          case '<300': return distance < 300;
          case '300-600': return distance >= 300 && distance <= 600;
          case '600-1000': return distance > 600 && distance <= 1000;
          case '>1000': return distance > 1000;
          default: return true;
        }
      });
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter(load =>
        load?.millName?.toLowerCase()?.includes(searchLower) ||
        load?.millCode?.toLowerCase()?.includes(searchLower) ||
        load?.grade?.toLowerCase()?.includes(searchLower) ||
        load?.offerId?.toLowerCase()?.includes(searchLower) ||
        load?.city?.toLowerCase()?.includes(searchLower) ||
        load?.stateName?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.minPrice) {
      result = result?.filter(load => load?.rate >= parseFloat(filters?.minPrice));
    }

    if (filters?.maxPrice) {
      result = result?.filter(load => load?.rate <= parseFloat(filters?.maxPrice));
    }

    result?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'date' || sortConfig?.key === 'paymentDate' || sortConfig?.key === 'liftingDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLoads(result);
  }, [filters, sortConfig, loads]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      state: 'all',
      mill: 'all',
      grade: 'all',
      distanceSlab: 'all',
      search: '',
      minPrice: '',
      maxPrice: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleBookSugarOnly = async (purchaseData) => {
    // 1. Auth + role check — booking is buyer-only; other logged-in roles
    // (factory_relations_officer, transporter, etc.) must not be able to purchase
    if (!user || !user?.id || !['buyer', 'admin'].includes(userProfile?.role)) {
      setShowGuestModal(true);
      return;
    }
    setQuotaCheckLoading(true);
    try {
      // 2. Re-fetch fresh quota status from Supabase
      let freshStatus = purchaseData?.availabilityStatus;
      if (purchaseData?.id) {
        const { data: quota } = await supabase?.from('quotas')?.select('availability_status')?.eq('id', purchaseData?.id)?.single();
        if (quota?.availability_status) freshStatus = quota?.availability_status;
      }
      // Open modal — pass fresh status so modal can show correct confirmation screen
      setSugarBookingModal({ isOpen: true, data: { ...purchaseData, availabilityStatus: freshStatus } });
    } finally {
      setQuotaCheckLoading(false);
    }
  };

  const handleConfirmSugarBooking = () => {
    // Do NOT close the modal here — SugarBookingModal shows the success popup
    // after confirmation and closes itself when user clicks "View Booking" or "Go to Buyer Dashboard"
  };

  const handleBookBundle = async (load) => {
    // 1. Auth + role check — booking is buyer-only; other logged-in roles
    // (factory_relations_officer, transporter, etc.) must not be able to purchase
    if (!user || !user?.id || !['buyer', 'admin'].includes(userProfile?.role)) {
      setShowGuestModal(true);
      return;
    }
    setQuotaCheckLoading(true);
    try {
      // 2. Quota approval check
      if (load?.availabilityStatus === 'pending_approval') {
        setPendingApprovalModal(true);
        return;
      }
      const noTrucksAvailable = load?.trucksAvailable === 0;
      setBundleBookingModal({ isOpen: true, data: load, noTrucks: noTrucksAvailable });
    } finally {
      setQuotaCheckLoading(false);
    }
  };

  const handleConfirmBundleBooking = () => {
    setBundleBookingModal({ isOpen: false, data: null, noTrucks: false });
  };

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      <Helmet>
        <title>Sugar Marketplace — Buy & Sell Sugar Offers | SugarHathi</title>
        <meta name="description" content="Browse live sugar buy and sell offers on SugarHathi's marketplace, connecting factories, traders, and buyers across India." />
      </Helmet>
      {/* Guest Login Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="LogIn" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>{t('marketplace.loginRequired', 'Login Required')}</h3>
            <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
              {t('marketplace.loginToBook', 'Please login or register to complete your booking.')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowGuestModal(false); navigate(`/login-authentication?next=${encodeURIComponent(window.location.pathname)}`); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                {t('common.login')}
              </button>
              <button
                onClick={() => { setShowGuestModal(false); navigate('/user-registration'); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold border"
                style={{ backgroundColor: '#ffffff', color: '#d97706', borderColor: '#d97706' }}
              >
                {t('common.register')}
              </button>
            </div>
            <button
              onClick={() => setShowGuestModal(false)}
              className="mt-4 text-xs"
              style={{ color: '#9ca3af' }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
      {/* Pending Approval Modal */}
      {pendingApprovalModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="Clock" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Offer Under Review</h3>
            <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
              This offer is under review by our team. You will be able to book once it is approved.
            </p>
            <button
              onClick={() => setPendingApprovalModal(false)}
              className="w-full py-2.5 rounded-xl text-sm font-bold"
              style={{ backgroundColor: '#d97706', color: '#ffffff' }}
            >
              OK, Got It
            </button>
          </div>
        </div>
      )}
      {/* Quota Check Loading Overlay */}
      {quotaCheckLoading && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
          <div className="rounded-2xl px-8 py-6 flex flex-col items-center gap-3" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-medium" style={{ color: '#374151' }}>Checking offer status...</p>
          </div>
        </div>
      )}
      <RoleBasedHeader />
      <main className="main-content">
        <BreadcrumbNavigation />
        
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          {/* Factory View Info Banner */}
          {userRole === 'factory' && (
            <div className="mb-6 rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.25)' }}>
              <Icon name="Info" size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#1d4ed8' }} />
              <div>
                <h3 className="text-sm font-semibold mb-1" style={{ color: '#1e40af' }}>{t('marketplace.factoryPreviewMode', 'Factory Preview Mode')}</h3>
                <p className="text-sm" style={{ color: '#1d4ed8' }}>
                  {t('marketplace.factoryPreviewDesc', "You're viewing the marketplace as buyers see it. This helps you understand how your sugar listings appear to potential customers.")}
                </p>
              </div>
            </div>
          )}

          {/* Mandi offer no longer resolvable — non-blocking, Marketplace stays fully usable */}
          {offerUnavailable && (
            <div className="mb-6 rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.25)' }}>
              <Icon name="Info" size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#b45309' }} />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: '#92400e' }}>
                  {t('marketplace.mandiOfferUnavailable', 'The selected offer is no longer available. Please browse the current Marketplace offers.')}
                </p>
              </div>
              <button onClick={() => setOfferUnavailable(false)} aria-label="Dismiss">
                <Icon name="X" size={16} style={{ color: '#92400e' }} />
              </button>
            </div>
          )}

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#1e293b' }}>{t('marketplace.title')}</h1>
              <p className="text-sm flex items-center gap-2 flex-wrap" style={{ color: '#475569' }}>
                <span className="flex items-center gap-1.5">
                  <Icon name="TrendingDown" size={13} style={{ color: '#d97706' }} />
                  {t('marketplace.competitivePrices', 'Competitive prices')}
                </span>
                <span style={{ color: '#94a3b8' }}>•</span>
                <span className="flex items-center gap-1.5">
                  <Icon name="Truck" size={13} style={{ color: '#2563eb' }} />
                  {t('marketplace.verifiedTrucksAvailable', 'Verified trucks available')}
                </span>
                <span style={{ color: '#94a3b8' }}>•</span>
                <span className="flex items-center gap-1.5">
                  <Icon name="Shield" size={13} style={{ color: '#0891b2' }} />
                  {t('marketplace.securePayments', 'Secure Payments')}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                size="default"
                iconName="RefreshCw"
                iconPosition="left"
                className="bg-amber-400 hover:bg-amber-500 text-white border-0"
                onClick={fetchLoads}
              >
                {t('common.refresh', 'Refresh')}
              </Button>
              <div className="flex items-center gap-1 rounded-xl p-1" style={{ background: 'rgba(0,0,0,0.07)' }}>
                <button
                  onClick={() => setViewMode('table')}
                  className="p-2.5 rounded-lg transition-all duration-150"
                  style={viewMode === 'table' ? { background: '#d97706', color: '#ffffff' } : { color: '#64748b' }}
                  aria-label="Table view"
                >
                  <Icon name="Table" size={18} />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className="p-2.5 rounded-lg transition-all duration-150"
                  style={viewMode === 'cards' ? { background: '#d97706', color: '#ffffff' } : { color: '#64748b' }}
                  aria-label="Card view"
                >
                  <Icon name="LayoutGrid" size={18} />
                </button>
              </div>
            </div>
          </div>

          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            resultsCount={filteredLoads?.length}
            availableMills={availableMills}
          />

          {/* Loading state */}
          {loading ? (
            <div className="rounded-2xl p-16 text-center border" style={{ background: '#ffffff', borderColor: 'rgba(0,0,0,0.1)' }}>
              <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-sm font-medium" style={{ color: '#64748b' }}>Loading marketplace data...</p>
            </div>
          ) : loadError ? (
            <div className="rounded-2xl p-12 text-center border" style={{ background: '#fff5f5', borderColor: 'rgba(220,38,38,0.2)' }}>
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4" style={{ color: '#dc2626' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#1e293b' }}>Failed to load data</h3>
              <p className="text-sm mb-4" style={{ color: '#64748b' }}>{loadError}</p>
              <Button variant="default" onClick={fetchLoads} iconName="RefreshCw" iconPosition="left">
                Retry
              </Button>
            </div>
          ) : filteredLoads?.length === 0 ? (
            <div className="rounded-2xl p-16 text-center border" style={{ background: '#ffffff', borderColor: 'rgba(0,0,0,0.1)' }}>
              <Icon name="PackageX" size={64} className="mx-auto mb-4" style={{ color: '#94a3b8' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#1e293b' }}>{t('marketplace.noListingsFound', 'No listings found')}</h3>
              <p className="mb-6" style={{ color: '#64748b' }}>
                {loads?.length === 0
                  ? t('marketplace.noFactoriesYet', 'No active quota listings found. Please add quotas via the Quota Manager.')
                  : t('marketplace.adjustFilters', 'Try adjusting your filters to find available sugar listings')}
              </p>
              <Button
                variant="outline"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleResetFilters}
              >
                {t('marketplace.resetAllFilters', 'Reset All Filters')}
              </Button>
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <div className="hidden lg:block">
                  <MarketplaceTable
                    loads={filteredLoads}
                    onBookSugarOnly={handleBookSugarOnly}
                    onBookBundle={handleBookBundle}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    highlightedLoadId={highlightedLoadId}
                  />
                </div>
              ) : null}
              
              {viewMode === 'cards' || viewMode === 'table' ? (
                <div className={viewMode === 'table' ? 'lg:hidden' : ''}>
                  <MarketplaceCards
                    loads={filteredLoads}
                    onBookSugarOnly={handleBookSugarOnly}
                    onBookBundle={handleBookBundle}
                    highlightedLoadId={highlightedLoadId}
                  />
                </div>
              ) : null}
            </>
          )}

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-2xl p-6 border" style={{ background: '#ffffff', borderColor: 'rgba(217,119,6,0.3)' }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(217,119,6,0.12)' }}>
                  <Icon name="Truck" size={24} style={{ color: '#d97706' }} />
                </div>
                <div>
                  <h3 className="text-base font-semibold" style={{ color: '#1e293b' }}>{t('marketplace.truckMarketplaceTitle', 'Truck Marketplace')}</h3>
                  <p className="text-sm" style={{ color: '#64748b' }}>{t('marketplace.findVerifiedTransporters', 'Find verified transporters')}</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/truck-marketplace')}
                className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-opacity hover:opacity-90"
                style={{ background: '#d97706', color: '#ffffff' }}
              >
                {t('marketplace.browseTrucks', 'Browse Trucks')}
                <Icon name="ArrowRight" size={16} style={{ color: '#ffffff' }} />
              </button>
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <SugarBookingModal
        isOpen={sugarBookingModal?.isOpen}
        onClose={() => setSugarBookingModal({ isOpen: false, data: null })}
        purchaseData={sugarBookingModal?.data}
        onConfirm={handleConfirmSugarBooking}
        quotaStatus={sugarBookingModal?.data?.availabilityStatus}
      />
      <BundleBookingModal
        isOpen={bundleBookingModal?.isOpen}
        onClose={() => setBundleBookingModal({ isOpen: false, data: null, noTrucks: false })}
        load={bundleBookingModal?.data}
        noTrucksAvailable={bundleBookingModal?.noTrucks}
        onConfirm={handleConfirmBundleBooking}
      />
      <PaymentInfoModal
        isOpen={paymentInfoModal}
        onClose={() => setPaymentInfoModal(false)}
      />
    </div>
  );
};

export default SugarMarketplaceDashboard;